import { isEqual } from 'lodash';
import { useContext, useEffect, useRef } from 'react';

import { AdditionalInfo, Category, Choice } from 'api/api';

import { DataChangedEventData, DataChangedEventMeta } from '~src/bootstrap/types';
import { GlobalStateContext, useGlobalState, useGlobalStateQuery } from '~src/global-state';
import { useIpc } from '~src/utils/ipc-utils';

export function useDataChangedEventDataEmitter(session_id: string) {
  const prevChoice = useRef<Choice>();
  const prevPayloadRef = useRef<DataChangedEventData | undefined>(undefined);
  const stateQuery = useGlobalStateQuery();

  const ipc = useIpc();

  useEffect(() => {
    const session = stateQuery.data?.sessions.find(session => session.session_id === session_id);

    if (!session) {
      return;
    }

    const choice = session.user_choice ?? session.preselected_choice;

    const selectedCategory = session.categories.find(
      category => category.id === choice.shipping_category
    );

    /*
     * Note: it is possible that preselected category does not exist.
     * If you want to prevent it set this flag in COS:
     *
     * "delete_pickup_options_without_location": true
     */
    if (!selectedCategory) {
      return;
    }

    const selectedOption = selectedCategory.options.find(
      option =>
        option.shipping_method === choice.shipping_method &&
        option.location?.id === choice.location_ref
    )!;

    const data: DataChangedEventData = {
      delivery_type: selectedCategory.delivery_type,
      external_method_id: selectedOption.external_method_id,
      price: selectedOption?.price.amount,
      shipping_method: selectedOption!.shipping_method,
      search_address: stateQuery.data!.search_address,
      category_name: selectedCategory.name,
      pickup_location: selectedOption.location
        ? { address: selectedOption.location.address, name: selectedOption.location.name }
        : undefined,
    };

    const meta: DataChangedEventMeta = {
      initial_load: !prevPayloadRef.current,
      delivery_type_changed: data.delivery_type !== prevPayloadRef.current?.delivery_type,
      external_method_id_changed:
        data.external_method_id !== prevPayloadRef.current?.external_method_id,
      price_changed: data.price !== prevPayloadRef.current?.price,
      search_address_changed: !isEqual(data.search_address, prevPayloadRef.current?.search_address),
      shipping_method_changed: data.shipping_method !== prevPayloadRef.current?.shipping_method,
      category_name_changed: data.category_name !== prevPayloadRef.current?.category_name,
      pickup_location_changed: !isEqual(
        data.pickup_location,
        prevPayloadRef.current?.pickup_location
      ),
    };

    if (
      meta.search_address_changed ||
      meta.price_changed ||
      meta.delivery_type_changed ||
      meta.shipping_method_changed ||
      meta.external_method_id_changed ||
      meta.category_name_changed ||
      meta.pickup_location_changed
    ) {
      ipc.send({
        name: 'data_changed',
        data: [data, meta],
      });
    }

    if (meta.search_address_changed) {
      ipc.send({
        name: 'address_changed',
        data: data.search_address!,
      });
    }

    if (!isEqual(prevChoice.current, choice)) {
      ipc.send({
        name: 'shipping_option_changed',
        data: { price: selectedOption?.price.amount, currency: selectedOption?.price.currency },
      });
    }

    prevChoice.current = choice;
    prevPayloadRef.current = data;
  }, [stateQuery.dataUpdatedAt, session_id]);
}

export function useAdditionalInfoEventEmitter() {
  const ipc = useIpc();
  // TODO: Investigate why globalstate doesn't receive a default value
  const { additional_info } = useContext(GlobalStateContext) || {};
  const prevAdditionalInfoRef = useRef<AdditionalInfo | undefined>();

  useEffect(() => {
    if (!isEqual(prevAdditionalInfoRef.current, additional_info)) {
      if (
        additional_info?.courier_instructions &&
        additional_info?.courier_instructions !==
          prevAdditionalInfoRef.current?.courier_instructions
      ) {
        ipc.send({
          name: 'courier_instructions_changed',
          data: additional_info?.courier_instructions,
        });
      }

      if (
        additional_info?.door_code &&
        additional_info?.door_code !== prevAdditionalInfoRef.current?.door_code
      ) {
        ipc.send({
          name: 'door_code_changed',
          data: additional_info?.door_code,
        });
      }

      if (
        additional_info?.customer_number &&
        additional_info?.customer_number !== prevAdditionalInfoRef.current?.customer_number
      ) {
        ipc.send({
          name: 'dhl_customer_number_changed',
          data: additional_info?.customer_number,
        });
      }
    }
    prevAdditionalInfoRef.current = additional_info;
  }, [
    additional_info?.courier_instructions,
    additional_info?.customer_number,
    additional_info?.door_code,
  ]);
}

export const useSplitDeliveryEventEmitter = () => {
  const globalState = useGlobalState();
  const ipc = useIpc();
  const { sessions } = globalState;

  // if there is more than one session that means we have split delivery
  // each session is one delivery group
  if (sessions.length > 1) {
    ipc.send({
      name: 'split_delivery',
      data: { number_of_delivery_groups: sessions.length },
    });
  }
};

export const useNoShippingOptionsEventEmitter = (session_id: string) => {
  const ipc = useIpc();
  const stateQuery = useGlobalStateQuery();
  const previousShippingCategories = useRef<Category[] | undefined>(undefined);

  useEffect(() => {
    const session = stateQuery.data?.sessions.find(session => session.session_id === session_id);

    if (!session) {
      return;
    }

    const emptyCategories = session.categories.length === 0;

    if (emptyCategories) {
      ipc.send({
        name: 'no_shipping_options',
        data: session.session_id,
      });
    }

    previousShippingCategories.current = session.categories;
  }, [stateQuery.dataUpdatedAt, session_id]);
};
